import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useNumericInput from "../../hooks/useNumericInput";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import CartItemsinCheckout from "../canvas/CartItemsinCheckout";
import { useCart } from "../../context/CartContext";
import Breadcrump from "../../components/Breadcrump";
import Summary from "./common/Summary";
import addressAPI from "../../api/addressApi";
import { useCheckout } from "../../hooks/useCheckout";
import { showError, showSuccess } from "../../utils/toastUtils";
import PaymentService from "./gateways/PaymentServices";
import { useSiteConfig } from "../../context/SiteConfigContext";

function Checkout() {
  useNumericInput();
  useAlphabeticInput();
  const { cartItems, fetchCart, cartSummary } = useCart();
  const { config } = useSiteConfig();
  const navigate = useNavigate();

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState("contact");

  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState("");

  const paymentMethods = [
    // {
    //   code: "cod",
    //   name: "Cash on Delivery",
    // },
    {
      code: "razorpay",
      name: "Pay with Razorpay",
    },
    // {
    //   code: "payu",
    //   name: "Pay with PayU",
    // },
  ];

  /****************** STATE DECLARATION *********/
  const auth = JSON.parse(localStorage.getItem("token") ?? "{}");

  const INITIAL_FORM_DATA = {
    email: auth.email || sessionStorage.getItem("emailId") || "",
    phone: sessionStorage.getItem("phoneNum") || "",
    firstName: "",
    lastName: "",
    address: {
      addressType: "home",
      addressLine1: "",
      addressLine2: "",
      pinCode: "",
      district: "",
      state: "",
      country: "",
    },
    paymentMethod: "razorpay",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  const { placeOrder } = useCheckout(cartItems, formData);

  /****************** STATE CHANGE *********/
  const handleChange = (e) => {
    const { name, value } = e.target;

    setSelectedAddress("");

    if (name.includes(".")) {
      const [parent, child] = name.split(".");

      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  /****************** PAYMENT METHOD *********/
  // useEffect(() => {
  //   navigator.geolocation.getCurrentPosition(
  //     (position) => {
  //       const { latitude, longitude } = position.coords;
  //       fetchPincodeFromCoordinates(latitude, longitude);
  //     },
  //     (error) => {
  //       console.error("Geolocation error:", error);
  //     },
  //   );
  // }, []);

  // const fetchPincodeFromCoordinates = async (lat, lng) => {
  //   try {
  //     const res = await userAPI.getAddressUsingLatLong({ lat, lng });

  //     if (res.code === 200) {
  //       const pincode = res?.data;
  //       if (pincode) {
  //         setPinCode(pincode);
  //       }
  //     }
  //   } catch (error) {
  //     console.error("Reverse geocoding error:", error);
  //   }
  // };

  /**************** CONTACT VALIDATION **************/

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^[6-9]\d{9}$/;
  const isContactValid = !!formData.email?.trim() && !!formData.phone?.trim();

  const validate = () => {
    const errors = {};

    if (!formData.email) {
      errors.email = "Please Enter Email Address";
    } else if (!emailRegex.test(formData.email)) {
      errors.email = "Please enter a valid email";
    }

    if (!formData.phone) {
      errors.phone = "Enter Phone Number";
    } else if (!phoneRegex.test(formData.phone)) {
      errors.phone = "Phone must be of 10 digits";
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    if (formData.email && formData.phone) {
      if (!validate()) {
        return;
      }

      getAllMyAddress();
      setActive("address");
    }
  }, []);

  /**************** SESSION STORAGE **************/
  const saveSessionData = () => {
    const { email, phone } = formData;

    email
      ? sessionStorage.setItem("emailId", email)
      : sessionStorage.removeItem("emailId");

    phone
      ? sessionStorage.setItem("phoneNum", phone)
      : sessionStorage.removeItem("phoneNum");
  };

  /**************** HANDLE CONTINUE **************/
  const handleContinue = (e) => {
    e.preventDefault();

    saveSessionData();

    if (!validate()) return;

    setActive("address");
    getAllMyAddress();
  };

  /**************** FETCH ADDRESS **************/
  const getAllMyAddress = async () => {
    try {
      const res = await addressAPI.fetchAllAddress({ email: formData.email });
      if (res.code === 200) {
        setSavedAddresses(res.data);

        setFormData((prev) => ({
          ...prev,
          firstName: res.data.firstName || "",
          lastName: res.data.lastName || "",
        }));
      }
    } catch (err) {}
  };

  /**************** SELECT SAVED ADDRESS **************/
  const handleSavedAddressSelect = (address) => {
    setSelectedAddress(address._id);

    setFormData((prev) => ({
      ...prev,
      firstName: address.firstName || "",
      lastName: address.lastName || "",
      address: {
        addressType: address.addressType || "home",
        addressLine1: address.addressLine1 || "",
        addressLine2: address.addressLine2 || "",
        pinCode: address.pinCode || "",
        district: address.district || "",
        state: address.state || "",
        country: address.country || "",
      },
    }));
  };

  /****************** PINCODE DATA *********/
  useEffect(() => {
    if (formData.address?.pinCode?.length === 6) {
      fetchPincodeDetails(formData.address?.pinCode);
    }
  }, [formData.address?.pinCode]);

  const fetchPincodeDetails = async (pincode) => {
    try {
      const res = await fetch(
        `https://api.postalpincode.in/pincode/${pincode}`,
      );

      const data = await res.json();

      if (data[0].Status === "Success") {
        const postOffice = data[0].PostOffice[0];

        setFormData((prev) => ({
          ...prev,
          address: {
            ...prev.address,
            state: postOffice.State,
            district: postOffice.District,
            country: postOffice.Country,
          },
        }));
      }
    } catch (error) {
      console.error("Error fetching pincode:", error);
    }
  };

  /****************** ADDRESS VALIDATION *********/
  const addressvalidate = () => {
    const requiredFields = [
      "firstName",
      "addressType",
      "addressLine1",
      "pinCode",
      "district",
      "state",
      "country",
    ];

    const errors = {};

    requiredFields.forEach((field) => {
      if (field === "firstName") {
        if (!formData.firstName) {
          errors[field] = `Please Enter ${field}`;
        }
      } else {
        if (!formData.address[field]) {
          errors[field] = `Please Enter ${field}`;
        }
      }
    });

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!addressvalidate()) {
        setLoading(false);
        return;
      }

      const action = await placeOrder();

      if (!action) {
        throw new Error("Something went wrong");
      }

      if (action.type === "COD_SUCCESS") {
        const { orderId } = action.payload;

        showSuccess(action.message);

        sessionStorage.setItem("orderId", orderId);
        sessionStorage.removeItem("emailId");
        sessionStorage.removeItem("phoneNum");

        // localStorage.setItem(
        //   "token",
        //   JSON.stringify({
        //     email: res.data.email,
        //     username: res.data.username,
        //   }),
        // );

        // localStorage.setItem("accessToken", res.data.accessToken);

        navigate("/order-confirmation");
      }

      if (action.type === "PAYMENT_STARTED") {
        const { orderId, paymentMethod, gatewayOrderId, amount, currency } =
          action.payload;

        PaymentService.pay(paymentMethod.toLowerCase(), {
          gatewayOrderId,
          orderId,
          amount,
          currency,
          formData,
          navigate,
          config,
          fetchCart,
        });
      }
    } catch (err) {
      console.error(err);
      showError(err.message || "Order failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Breadcrump title="Checkout" />

      <div className="flat-spacing-13 med-checkout-page">
        <div className="container-fluid px-lg-5 px-3">
          <div className="row">
            <div className="col-xl-8">
              <div className="tf-product-side-accordions">
                <div className="widget-accordion wd-product-descriptions">
                  <div
                    className={`accordion-title ${
                      active !== "contact" ? "collapsed" : ""
                    }`}
                    role="button"
                    onClick={() => {
                      if (active === "address" && !isContactValid) return;
                      setActive("contact");
                    }}
                  >
                    <span>
                      Share your Contact Details
                      {active === "address" && (
                        <span className="m-3 text-white">
                          ({formData.email}, {formData.phone})
                        </span>
                      )}
                    </span>
                    <span className="icon icon-arrow-down" />
                  </div>
                  <div
                    id="contact"
                    className={`collapse ${active === "contact" ? "show" : ""}`}
                  >
                    <div className="tf-checkout-cart-main accordion-body widget-desc">
                      <div className="box-ip-checkout">

                        <form method="post" onSubmit={handleContinue}>
                          <div className="grid-3 mb_16">
                            <fieldset className="tf-field style-2 style-3">
                              <input
                                className="tf-field-input tf-input"
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                              />
                              <label className="tf-field-label" htmlFor="city">
                                Email Address
                              </label>
                              {errors && errors.email && (
                                <span className="text-danger custom-input-span-text">
                                  {errors.email}
                                </span>
                              )}
                            </fieldset>
                            <fieldset className="tf-field style-2 style-3">
                              <input
                                className="tf-field-input tf-input only-numeric"
                                type="text"
                                name="phone"
                                value={formData.phone}
                                maxLength={10}
                                onChange={handleChange}
                              />
                              <label className="tf-field-label" htmlFor="city">
                                Phone Number
                              </label>
                              {errors && errors.phone && (
                                <span className="text-danger custom-input-span-text">
                                  {errors.phone}
                                </span>
                              )}
                            </fieldset>
                            <button className=" shop-btn">CONTINUE</button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Accordion 2: Address Details */}
                <div className="widget-accordion wd-product-descriptions">
                  <div
                    className={`accordion-title ${
                      active !== "address" ? "collapsed" : ""
                    }`}
                    role="button"
                    onClick={() => {
                      if (!isContactValid) return; // Do nothing until form is filled
                      setActive("address");
                    }}
                  >
                    <span>Enter your Address</span>
                    <span className="icon icon-arrow-down" />
                  </div>
                  <div
                    id="address"
                    className={`collapse ${active === "address" ? "show" : ""}`}
                  >
                    <div className="tf-checkout-cart-main accordion-body widget-desc">
                      <div className="item">
                        {savedAddresses.length > 0 && (
                          <>
                            <b>Select a saved address:</b>
                            <div
                              className="payment-method-box"
                              id="payment-method-box"
                            >
                              {savedAddresses.map((address, index) => (
                                <div
                                  className="payment-item mb_16"
                                  key={address._id}
                                  onClick={(e) =>
                                    handleSavedAddressSelect(address)
                                  }
                                >
                                  <label
                                    htmlFor="delivery"
                                    className="payment-header collapsed"
                                  >
                                    <input
                                      type="radio"
                                      className="tf-check-rounded"
                                      name="savedAddress"
                                      checked={selectedAddress === address._id}
                                      onChange={() =>
                                        handleSavedAddressSelect(address)
                                      }
                                    />
                                    <span className="pay-title text-sm">
                                      <b>{address.addressType} : </b>
                                      {address.firstName} {address.lastName}
                                      <br />
                                      {address.addressLine1}
                                      {", "}
                                      {address.addressLine2}, {address.district}
                                      , {address.state} - {address.pinCode}{" "}
                                      <br />
                                      {address.country}
                                    </span>
                                  </label>
                                </div>
                              ))}
                            </div>
                          </>
                        )}

                        <div className="box-ip-checkout">
                          <div className="title text-lg fw-medium">
                            Address Details
                          </div>
                          <div className="grid-2 mb_16">
                            <div className="tf-field style-2 style-3">
                              <input
                                className="tf-field-input tf-input only-alphabets"
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                              />
                              <label
                                className="tf-field-label"
                                htmlFor="firstname"
                              >
                                First name
                                <span className="text-danger">*</span>
                              </label>
                              {errors && errors.firstName && (
                                <span className="text-danger custom-input-span-text">
                                  {errors.firstName}
                                </span>
                              )}
                            </div>
                            <div className="tf-field style-2 style-3">
                              <input
                                className="tf-field-input tf-input only-alphabets"
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                              />
                              <label
                                className="tf-field-label"
                                htmlFor="lastname"
                              >
                                Last name
                              </label>
                            </div>
                          </div>

                          <label className="tf-field-label">
                            Address Type
                            <span className="text-danger">*</span>
                          </label>
                          <div className="grid-3">
                            <fieldset className="mb_16">
                              <label htmlFor="homeship" className="check-ship">
                                <input
                                  id="homeship"
                                  type="radio"
                                  className="tf-check-rounded"
                                  value="home"
                                  name="address.addressType"
                                  checked={
                                    formData.address.addressType === "home"
                                  }
                                  onChange={handleChange}
                                />
                                <span className="text text-sm">Home</span>
                              </label>
                            </fieldset>
                            <fieldset>
                              <label
                                htmlFor="officeship"
                                className="check-ship"
                              >
                                <input
                                  id="officeship"
                                  type="radio"
                                  className="tf-check-rounded"
                                  value="work"
                                  name="address.addressType"
                                  checked={
                                    formData.address.addressType === "work"
                                  }
                                  onChange={handleChange}
                                />
                                <span className="text text-sm">Work</span>
                              </label>
                            </fieldset>
                            <fieldset>
                              <label htmlFor="othership" className="check-ship">
                                <input
                                  id="othership"
                                  type="radio"
                                  className="tf-check-rounded"
                                  value="other"
                                  name="address.addressType"
                                  checked={
                                    formData.address.addressType === "other"
                                  }
                                  onChange={handleChange}
                                />
                                <span className="text text-sm">Other</span>
                              </label>
                            </fieldset>
                            {errors && errors.addressType && (
                              <span className="text-danger  custom-input-span-text">
                                {errors.addressType}
                              </span>
                            )}
                          </div>

                          <fieldset className="tf-field style-2 style-3 mb_16">
                            <input
                              className="tf-field-input tf-input"
                              type="text"
                              name="address.addressLine1"
                              value={formData.address.addressLine1}
                              onChange={handleChange}
                            />
                            <label className="tf-field-label" htmlFor="address">
                              Address <span className="text-danger">*</span>
                            </label>
                            {errors && errors.addressLine1 && (
                              <span className="text-danger custom-input-span-text">
                                {errors.addressLine1}
                              </span>
                            )}
                          </fieldset>
                          <fieldset className="tf-field style-2 style-3 mb_16">
                            <input
                              type="text"
                              name="address.addressLine2"
                              className="tf-field-input tf-input"
                              value={formData.address.addressLine2}
                              onChange={handleChange}
                            />
                            <label
                              className="tf-field-label"
                              htmlFor="apartment"
                            >
                              Apartment, suite, etc (optional)
                            </label>
                          </fieldset>
                          <div className="grid-3 mb_16">
                            <fieldset className="tf-field style-2 style-3">
                              <input
                                className="tf-field-input tf-input only-numeric"
                                maxLength={6}
                                name="address.pinCode"
                                type="text"
                                value={formData.address.pinCode}
                                onChange={handleChange}
                              />
                              <label className="tf-field-label" htmlFor="code">
                                Zipcode/Postal
                                <span className="text-danger">*</span>
                              </label>
                              {errors && errors.pinCode && (
                                <span className="text-danger custom-input-span-text">
                                  {errors.pinCode}
                                </span>
                              )}
                            </fieldset>
                            <fieldset className="tf-field style-2 style-3">
                              <input
                                className="tf-field-input tf-input only-alphabets"
                                type="text"
                                name="address.district"
                                value={formData.address.district}
                                onChange={handleChange}
                              />
                              <label className="tf-field-label" htmlFor="city">
                                District <span className="text-danger">*</span>
                              </label>
                              {errors && errors.district && (
                                <span className="text-danger custom-input-span-text">
                                  {errors.district}
                                </span>
                              )}
                            </fieldset>
                            <fieldset className="tf-field style-2 style-3">
                              <input
                                className="tf-field-input tf-input only-alphabets"
                                type="text"
                                name="address.state"
                                value={formData.address.state}
                                onChange={handleChange}
                              />
                              <label className="tf-field-label" htmlFor="city">
                                State <span className="text-danger">*</span>
                              </label>
                              {errors && errors.state && (
                                <span className="text-danger custom-input-span-text">
                                  {errors.state}
                                </span>
                              )}
                            </fieldset>
                          </div>
                          <fieldset className="tf-field style-2 style-3">
                            <input
                              className="tf-field-input tf-input only-alphabets"
                              type="text"
                              name="address.country"
                              value={formData.address.country}
                              onChange={handleChange}
                            />
                            <label className="tf-field-label" htmlFor="city">
                              Country <span className="text-danger">*</span>
                            </label>
                            {errors && errors.country && (
                              <span className="text-danger custom-input-span-text">
                                {errors.country}
                              </span>
                            )}
                          </fieldset>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-4">
              <div className="tf-page-cart-sidebar">
                <div className="cart-box order-box">
                  <div className="cart-summary-container">
                    <div className="title text-lg fw-medium">In your cart</div>

                    {cartItems && cartItems.length > 0 && (
                      <ul className="list-order-product">
                        {cartItems.map((item) => (
                          <CartItemsinCheckout item={item} key={item._id} />
                        ))}
                      </ul>
                    )}

                    <Summary cartItems={cartItems} cartSummary={cartSummary} />

                    <hr />

                    <div className="title text-lg fw-medium">
                      Payment Method
                    </div>

                    <div className="payment-method-box" id="payment-method-box">
                      {paymentMethods.map((method) => (
                        <div className="payment-item mb_16" key={method.code}>
                          <label
                            htmlFor={method.code}
                            className="payment-header collapsed"
                          >
                            <input
                              type="radio"
                              name="paymentMethod"
                              className="tf-check-rounded"
                              id={method.code}
                              value={method.code}
                              checked={formData.paymentMethod === method.code}
                              onChange={handleChange}
                            />

                            <span className="pay-title text-sm">
                              {method.name}
                            </span>
                          </label>
                        </div>
                      ))}
                    </div>

                    <div className="btn-order">
                      <button
                        className="shop-btn w-100 text-transdiv-none d-flex align-items-center justify-content-center"
                        onClick={handlePlaceOrder}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            />
                            Processing...
                          </>
                        ) : (
                          "Place Order"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Checkout;
